import type { RecordedBy } from '../product/recorded-by.js'

/**
 * Um **recado** deixado na casa.
 *
 * ## O que ele é, e o que ele NÃO é
 *
 * Ele mora na tela de notificações e não é uma notificação. Uma notificação é
 * gerada pelo sistema, pertence a **um destinatário**, tem entrega e push, e
 * `firestore.rules` proíbe o cliente de criá-la — *"resto é criado pelo
 * `NotificationService` (Admin SDK)"*. Um recado é o oposto em cada um desses
 * eixos: escrito por gente, é da **casa** inteira, e não notifica ninguém.
 *
 * Torcer o modelo de notificação para caber os dois custaria a invariante que
 * aquela regra protege. São duas coleções lidas pela mesma tela, e a tela junta
 * as duas por data — que é como elas se parecem de verdade: coisas que
 * aconteceram, em ordem.
 *
 * ## Para que ele existe: o agente precisa de onde deixar o achado
 *
 * O contexto de agentes (`instructions.ts`) é o que um agente **lê** antes de
 * caçar preço. Faltava o caminho de volta — o resultado, o detalhe, o link. É
 * um bloco de texto colado, e é por isso que `texto` é livre e não tem forma:
 * uma lista de links, um parágrafo, uma tabela em markdown, tudo cabe.
 *
 * ## O autor é uma DECLARAÇÃO, e o `createdBy` é o fato
 *
 * Um agente **escreve aqui**, e escreve com a credencial da pessoa: ele controla
 * o navegador, a sessão já está aberta, e ele usa a tela como qualquer um usaria.
 * Não há login de agente neste app, e não precisa haver.
 *
 * A consequência é que `createdBy` vai ser sempre o uid de uma pessoa, e nenhuma
 * regra pode desmentir isso. `autor` diz de quem é o **conteúdo**; `createdBy`
 * diz quem o **pôs aqui**. Guardar os dois separados é o que impede a etiqueta
 * "achado por um agente" de virar uma afirmação que o sistema não pode
 * sustentar — e é o que permite ela existir: a honestidade vem da etiqueta, não
 * de uma verificação que não há como fazer.
 *
 * É o mesmo par de `PricePoint`: `recordedBy` ao lado do dono do documento, e a
 * mesma razão — *"as duas coisas merecem confiança diferente"*.
 */
export type Recado = {
  id: string
  householdId: string
  /** Texto livre. Markdown é bem-vindo e não é interpretado. */
  texto: string
  /** De quem é o conteúdo. Ver o cabeçalho — é declaração, não prova. */
  autor: RecordedBy
  /** O uid de quem gravou. Sempre uma pessoa. Carimbado pelo adapter. */
  createdBy: string
  /** Instante ISO 8601. */
  createdAt: string
}

/** O que a tela manda gravar. O resto o adapter carimba. */
export type NovoRecado = {
  texto: string
  autor: RecordedBy
}

/** Vazio não vira recado: um item em branco na lista é ruído sem conteúdo. */
export function recadoValido(texto: string): boolean {
  return texto.trim() !== ''
}
